import React, { useMemo, useState } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useFlowObserverPanelStore } from '@flow-studio/flow-observer-core';
import type { FlowObserverTabContentProps, ExecutionLogEntry } from '@flow-studio/flow-observer-core';
import { DataItemsView } from '../common/DataItemsView.js';
import './ErrorAnalysisDashboard.css';

interface ErrorDetail {
  id: string;
  nodeName: string;
  nodeId: number;
  stage: string;
  message: string;
  timestamp: string;
  entityType: string;
  entry: ExecutionLogEntry;
}

function extractFailedEntities(logs: ExecutionLogEntry[]): ErrorDetail[] {
  // `rawDetail.status.runState` never exists on a real entry — `runState` is a field the
  // frontend only ever constructs on the separate execution-status buffer's own
  // ExecutionStatusData type (see useFlowObserverSignalR.ts); the raw backend payload's
  // `status` only ever carries `.name`/`.startedAt`/`.completedAt`/`.duration`. `level`
  // is the reliable signal — it's set deterministically by the frontend itself whenever
  // it processes an ExecutionError/WorkflowCompleted-with-failure event.
  return logs
    .filter((l) => l.level === 'error' || (l.rawDetail as any)?.error != null)
    .map((l) => ({
      id: `${l.id}`,
      nodeName: (l.rawDetail as any)?.item?.name || 'Unknown',
      nodeId: (l.rawDetail as any)?.item?.id || 0,
      stage: (l.eventInfo as any)?.eventDescription?.stage || 'Unknown',
      message: l.message || 'No error details',
      timestamp: l.timestamp,
      entityType: (l.rawDetail as any)?.item?.entityType || 'Unknown',
      entry: l,
    }));
}

const renderStageLabel = (props: any) => {
  const { x, y, textAnchor, fill, name, value } = props;
  const fontSize = 13;
  const lineHeight = fontSize * 1.2;
  const padding = 6;
  const nameWidth = String(name).length * fontSize * 0.55;
  const valueWidth = String(value).length * fontSize * 0.55;
  const centerX =
    textAnchor === 'end' ? x - nameWidth / 2 :
    textAnchor === 'start' ? x + nameWidth / 2 :
    x;
  const boxWidth = Math.max(nameWidth, valueWidth) + padding * 2;
  const boxHeight = lineHeight * 2 + padding * 2;

  return (
    <g>
      <rect
        x={centerX - boxWidth / 2}
        y={y - boxHeight / 2}
        width={boxWidth}
        height={boxHeight}
        rx={4}
        fill="#1f2937"
        stroke={fill}
        strokeWidth={1}
      />
      <text x={centerX} y={y} fill={fill} fontSize={fontSize} textAnchor="middle">
        <tspan x={centerX} dy="-0.2em">{name}</tspan>
        <tspan x={centerX} dy="1.2em">{value}</tspan>
      </text>
    </g>
  );
};

export const ErrorAnalysisDashboard: React.FC<FlowObserverTabContentProps> = ({
  tabId,
  loadState,
  error,
  onRetry,
}) => {
  const logBuffer = useFlowObserverPanelStore(
    (s) => s.tabRuntimeStates['execution-logs']?.dataBuffer ?? []
  );

  const [selectedStage, setSelectedStage] = useState<string>('all');
  const [selectedEntityType, setSelectedEntityType] = useState<string>('all');
  const [detailError, setDetailError] = useState<ErrorDetail | null>(null);

  const failedEntities = useMemo(() => {
    return extractFailedEntities(logBuffer as ExecutionLogEntry[]);
  }, [logBuffer]);

  const filteredErrors = useMemo(() => {
    return failedEntities.filter(
      (e) =>
        (selectedStage === 'all' || e.stage === selectedStage) &&
        (selectedEntityType === 'all' || e.entityType === selectedEntityType)
    );
  }, [failedEntities, selectedStage, selectedEntityType]);

  const metrics = useMemo(() => {
    return {
      total: failedEntities.length,
      byStage: failedEntities.reduce(
        (acc, e) => {
          acc[e.stage] = (acc[e.stage] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      ),
      byEntityType: failedEntities.reduce(
        (acc, e) => {
          acc[e.entityType] = (acc[e.entityType] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      ),
    };
  }, [failedEntities]);

  const stageChartData = useMemo(() => {
    return Object.entries(metrics.byStage).map(([stage, count]) => ({
      name: stage,
      value: count,
    }));
  }, [metrics]);

  const entityTypeChartData = useMemo(() => {
    return Object.entries(metrics.byEntityType).map(([type, count]) => ({
      name: type,
      value: count,
    }));
  }, [metrics]);

  const timelineData = useMemo(() => {
    const entries = failedEntities.slice(-30);
    const data: Record<string, number> = {};

    entries.forEach((e) => {
      const time = new Date(e.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      data[time] = (data[time] || 0) + 1;
    });

    return Object.entries(data).map(([time, count]) => ({
      time,
      errors: count,
    }));
  }, [failedEntities]);

  const stages = useMemo(() => {
    return Array.from(new Set(failedEntities.map((e) => e.stage)));
  }, [failedEntities]);

  const entityTypes = useMemo(() => {
    return Array.from(new Set(failedEntities.map((e) => e.entityType)));
  }, [failedEntities]);

  if (loadState === 'loading') {
    return <div className="fop-loading">Loading…</div>;
  }

  if (loadState === 'error' && error) {
    return (
      <div className="fop-error" role="alert">
        <span>{error.message}</span>
        {error.retryable && (
          <button className="fop-error__retry" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16'];

  return (
    <div className="error-dashboard">
      {/* Summary Metric */}
      <div className="error-dashboard__summary">
        <div className="error-dashboard__summary-card">
          <div className="error-dashboard__summary-label">Total Errors</div>
          <div className="error-dashboard__summary-value">{metrics.total}</div>
        </div>
        <div className="error-dashboard__summary-card">
          <div className="error-dashboard__summary-label">Stages with Errors</div>
          <div className="error-dashboard__summary-value">{stages.length}</div>
        </div>
        <div className="error-dashboard__summary-card">
          <div className="error-dashboard__summary-label">Entity Types Affected</div>
          <div className="error-dashboard__summary-value">{entityTypes.length}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="error-dashboard__filters">
        <div className="error-dashboard__filter-group">
          <label className="error-dashboard__filter-label">Stage:</label>
          <select
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value)}
            className="error-dashboard__filter-select"
          >
            <option value="all">All</option>
            {stages.map((stage) => (
              <option key={stage} value={stage}>
                {stage}
              </option>
            ))}
          </select>
        </div>

        <div className="error-dashboard__filter-group">
          <label className="error-dashboard__filter-label">Entity Type:</label>
          <select
            value={selectedEntityType}
            onChange={(e) => setSelectedEntityType(e.target.value)}
            className="error-dashboard__filter-select"
          >
            <option value="all">All</option>
            {entityTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Charts */}
      <div className="error-dashboard__charts">
        {/* Timeline */}
        {timelineData.length > 0 && (
          <div className="error-dashboard__chart">
            <h3 className="error-dashboard__chart-title">Error Timeline</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="time" stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563' }}
                  labelStyle={{ color: '#d1d5db' }}
                />
                <Line
                  type="monotone"
                  dataKey="errors"
                  stroke="#ef4444"
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Stage Distribution */}
        {stageChartData.length > 0 && (
          <div className="error-dashboard__chart">
            <h3 className="error-dashboard__chart-title">Errors by Stage</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={stageChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={renderStageLabel}
                  dataKey="value"
                >
                  {stageChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #4b5563',
                  }}
                  labelStyle={{ color: '#d1d5db' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Entity Type Distribution */}
        {entityTypeChartData.length > 0 && (
          <div className="error-dashboard__chart">
            <h3 className="error-dashboard__chart-title">Errors by Entity Type</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={entityTypeChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                <Tooltip
                  cursor={false}
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563' }}
                  labelStyle={{ color: '#d1d5db' }}
                />
                <Bar dataKey="value" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Error Details Table */}
      <div className="error-dashboard__details">
        <h3 className="error-dashboard__chart-title">Error Details</h3>
        <div className="error-dashboard__table-scroll">
          {filteredErrors.length === 0 ? (
            <div className="fop-empty">
              <span>No errors match the selected filters</span>
            </div>
          ) : (
            <table className="error-dashboard__table">
              <thead>
                <tr>
                  <th>Node</th>
                  <th>Stage</th>
                  <th>Type</th>
                  <th>Message</th>
                  <th>Time</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredErrors.map((err) => (
                  <tr key={err.id} onClick={() => setDetailError(err)} style={{ cursor: 'pointer' }}>
                    <td>{err.nodeName}</td>
                    <td>{err.stage}</td>
                    <td>{err.entityType}</td>
                    <td className="error-dashboard__table-message">
                      {err.message.substring(0, 60)}
                    </td>
                    <td className="error-dashboard__table-time">
                      {new Date(err.timestamp).toLocaleTimeString()}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="error-dashboard__details-btn"
                        onClick={(e) => { e.stopPropagation(); setDetailError(err); }}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {detailError && (
        <div className="error-dashboard__detail-overlay" onClick={() => setDetailError(null)}>
          <div className="error-dashboard__detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="error-dashboard__detail-modal__header">
              <span>{detailError.nodeName} — {detailError.stage}</span>
              <button type="button" onClick={() => setDetailError(null)} aria-label="Close">×</button>
            </div>
            <div className="error-dashboard__detail-modal__body">
              <DataItemsView items={[detailError.entry]} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
